import React from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
import { Loader2 } from 'lucide-react';

export interface ButtonProps extends Omit<HTMLMotionProps<"button">, "children"> {
    variant?: 'primary' | 'secondary' | 'ghost' | 'destructive' | 'link' | 'icon' | 'outline';
    size?: 'sm' | 'md' | 'lg';
    isLoading?: boolean;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    children?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({
        className = '',
        variant = 'primary',
        size = 'md',
        isLoading = false,
        leftIcon,
        rightIcon,
        children,
        disabled,
        ...props
    }, ref) => {

        const baseStyles = "inline-flex items-center justify-center rounded-xl font-semibold transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none tracking-tight";

        const variants = {
            primary: "bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-lg shadow-teal-500/30 hover:shadow-xl hover:shadow-teal-500/40 hover:from-teal-400 hover:to-teal-500 border border-transparent focus:ring-teal-500/20",
            secondary: "bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white border-2 border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600 shadow-sm hover:shadow-md focus:ring-zinc-500/20",
            outline: "bg-transparent border-2 border-teal-500 text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-950/30 hover:border-teal-600 focus:ring-teal-500/20",
            ghost: "bg-transparent text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-zinc-900 dark:hover:text-white focus:ring-zinc-500/20",
            destructive: "bg-gradient-to-r from-rose-500 to-rose-600 text-white shadow-lg shadow-rose-500/30 hover:shadow-xl hover:shadow-rose-500/40 hover:from-rose-400 hover:to-rose-500 focus:ring-rose-500/20",
            link: "bg-transparent text-teal-600 dark:text-teal-400 hover:underline p-0 h-auto font-normal shadow-none focus:ring-0",
            icon: "p-2 bg-transparent hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white rounded-full aspect-square",
        };

        const sizes = {
            sm: "h-9 px-4 text-xs gap-2 min-w-[44px]",
            md: "h-11 px-6 text-sm gap-2 min-w-[44px]",
            lg: "h-13 px-8 text-base gap-3 min-w-[44px]",
        };

        // Icon button override - ensure touch-friendly 44x44px minimum
        if (variant === 'icon') {
            sizes.sm = "h-11 w-11 p-0 min-w-[44px]";
            sizes.md = "h-11 w-11 p-0 min-w-[44px]";
            sizes.lg = "h-12 w-12 p-0 min-w-[44px]";
        }

        return (
            <motion.button
                ref={ref}
                whileHover={!disabled && !isLoading ? { scale: 1.02, y: -2 } : undefined}
                whileTap={!disabled && !isLoading ? { scale: 0.97 } : undefined}
                transition={{ duration: 0.15, ease: 'easeOut' }}
                className={`
          ${baseStyles}
          ${variants[variant]}
          ${variant !== 'link' ? sizes[size] : ''}
          ${className}
        `}
                disabled={disabled || isLoading}
                {...props}
            >
                {isLoading && (
                    <Loader2 className={`animate-spin ${children ? 'mr-2' : ''} ${size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'}`} />
                )}
                {!isLoading && leftIcon && (
                    <span className={children ? "mr-2" : ""}>{leftIcon}</span>
                )}
                {children}
                {!isLoading && rightIcon && (
                    <span className={children ? "ml-2" : ""}>{rightIcon}</span>
                )}
            </motion.button>
        );
    }
);

Button.displayName = "Button";

export default Button;

